/**
 * Compact storage for a document's editor snapshot (sync size fix, 14.10.2026).
 *
 * A docbody record used to store the Lexical block snapshot as `bodyLexical`
 * (`{ version: 1, blocks: [{ id, node }] }`, lexicalBlocks.js) — about five
 * times the size of the document's text, because Lexical exports every key of
 * every node, defaults included (`version: 1`, `detail: 0`, `mode: "normal"`,
 * `style: ""`, `indent: 0`, table-cell spans…). A 1 355-word page came to
 * 81 KB and could not sync.
 *
 * New writes store `bodyBlocks` instead: the same snapshot with every key that
 * equals its node type's default left out (lexicalNodeDefaults.js), and
 * `expandSnapshot` puts them back — EXACTLY: same keys, same values, same key
 * order as Lexical exported them, so the editor's own-echo check and block
 * diff (stableStringify / JSON.stringify of nodes) see what they saw before.
 *
 *   compact = { v: 2, blocks: [{ id, node }] }
 *
 * A compact node keeps every non-default key, and `type` unless it is implied
 * (`text` for a node with `text`, else `paragraph`). When a node LACKED a key
 * its template has a default for (a snapshot from an older Lexical), the key
 * is listed under `"~"` so expansion does not invent it.
 *
 * Old app versions read only `bodyLexical` and cannot parse a compact node
 * (Lexical's `updateFromJSON` does not fill missing keys), which is why this
 * is a NEW field and not a new shape for the old one: an old client simply
 * sees no snapshot and loads the body from `bodyHtml`, as it does for any
 * document it has never had blocks for. documentsStore.js is the only place
 * that converts.
 */
import { IMPLIED_TYPES, defaultValue, impliedType, isDefault, templateFor } from './lexicalNodeDefaults';

export const COMPACT_VERSION = 2;
const ABSENT = '~';

const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

function compactNode(node) {
  if (!isObject(node)) return node;
  const template = templateFor(node.type);
  const out = {};
  const absent = [];
  for (const [key, value] of Object.entries(node)) {
    if (value === undefined) continue; // JSON has no undefined: the key is not there
    if (template && template.defaults.has(key) && isDefault(value, template.defaults.get(key))) continue;
    out[key] = key === 'children' && Array.isArray(value) ? value.map(compactNode) : value;
  }
  if (template) {
    for (const key of template.defaults.keys()) {
      if (node[key] === undefined) absent.push(key);
    }
  }
  // `text` / `paragraph` are implied by the node's shape (impliedType).
  if (IMPLIED_TYPES.includes(out.type) && impliedType(out) === out.type) delete out.type;
  else if (node.type === undefined) absent.push('type');
  if (absent.length) out[ABSENT] = absent;
  return out;
}

function expandNode(node) {
  if (!isObject(node)) return node;
  const missing = new Set(Array.isArray(node[ABSENT]) ? node[ABSENT] : []);
  const type = has(node, 'type') || missing.has('type') ? node.type : impliedType(node);
  const template = templateFor(type);
  const out = {};
  const put = (key, value) => {
    out[key] = key === 'children' && Array.isArray(value) ? value.map(expandNode) : value;
  };
  if (template) {
    for (const key of template.order) {
      if (has(node, key)) put(key, node[key]);
      else if (key === 'type') put(key, type);
      else if (template.defaults.has(key) && !missing.has(key)) put(key, defaultValue(template.defaults.get(key)));
    }
  } else if (type !== undefined && !has(node, 'type')) {
    out.type = type;
  }
  for (const [key, value] of Object.entries(node)) {
    if (key === ABSENT || has(out, key)) continue;
    put(key, value);
  }
  return out;
}

/** One block's node, compact / expanded — a `docblock` record stores one (docBlocks.js). */
export const compactBlockNode = compactNode;
export const expandBlockNode = expandNode;

const isBlockList = (blocks) => Array.isArray(blocks);

/** `{version: 1, blocks}` -> `{v: 2, blocks}` (compact), or null. */
export function compactSnapshot(snapshot) {
  if (!isObject(snapshot) || !isBlockList(snapshot.blocks)) return null;
  const out = { v: COMPACT_VERSION, blocks: snapshot.blocks.map((b) => ({ id: b.id, node: compactNode(b.node) })) };
  // `sv`: a snapshot version other than 1 (null = the snapshot had none).
  if (snapshot.version !== 1) out.sv = snapshot.version === undefined ? null : snapshot.version;
  return out;
}

/** The inverse of `compactSnapshot`; null for anything that is not one. */
export function expandSnapshot(compact) {
  if (!isObject(compact) || compact.v !== COMPACT_VERSION || !isBlockList(compact.blocks)) return null;
  const blocks = compact.blocks.map((b) => ({ id: b.id, node: expandNode(b.node) }));
  if (!has(compact, 'sv')) return { version: 1, blocks };
  return compact.sv === null ? { blocks } : { version: compact.sv, blocks };
}

/**
 * A docbody record's `data` for an editor snapshot: the snapshot goes in
 * compact as `bodyBlocks` and `bodyLexical` is never written (a row holds one
 * or the other, so the two can never disagree). A value that is not a block
 * snapshot is kept verbatim as `bodyLexical` rather than dropped.
 */
export function docBodyData(docId, bodyHtml, bodyLexical) {
  const data = { scope_id: docId, bodyHtml: bodyHtml || '' };
  if (bodyLexical == null) return data;
  const compact = compactSnapshot(bodyLexical);
  if (compact) data.bodyBlocks = compact;
  else data.bodyLexical = bodyLexical;
  return data;
}

/**
 * A docbody record's `data` -> its editor snapshot: `bodyBlocks` when the row
 * has it (written by this version), else the legacy `bodyLexical` (written by
 * an older app, including one that re-saved after us), else null.
 */
export function snapshotFromBodyData(data) {
  if (!data) return null;
  if (data.bodyBlocks) {
    const expanded = expandSnapshot(data.bodyBlocks);
    if (expanded) return expanded;
  }
  return data.bodyLexical || null;
}
